import type { LayerRect } from './types';

/** Which guide an axis is currently snapped to. `start`/`end` are the
 *  composition's edges (left/right, top/bottom); `center` is its midline. */
export type SnapTarget = 'start' | 'center' | 'end';

export interface SnapGuides {
  x: SnapTarget | null;
  y: SnapTarget | null;
}

/** The composition-relative position each guide pulls toward. */
const GUIDE_POSITION: Record<SnapTarget, number> = { start: 0, center: 0.5, end: 1 };

/** Tried in this order, so an exact tie resolves to the center — the guide a
 *  user reaching for "dead centre" means. */
const TARGETS: SnapTarget[] = ['center', 'start', 'end'];

/** Where the rect's own `target` feature sits on this axis: its leading edge,
 *  its midpoint, or its trailing edge. */
const featureAt = (origin: number, size: number, target: SnapTarget): number =>
  target === 'start' ? origin : target === 'center' ? origin + size / 2 : origin + size;

/** The translation that lands `origin` on the nearest in-range guide, or 0. */
function snapAxis(
  origin: number,
  size: number,
  threshold: number,
): { delta: number; guide: SnapTarget | null } {
  let best: { delta: number; guide: SnapTarget | null } = { delta: 0, guide: null };
  for (const target of TARGETS) {
    const delta = GUIDE_POSITION[target] - featureAt(origin, size, target);
    // Strict `<`, so a zero threshold disables snapping outright rather than
    // latching onto whatever the rect is already flush with.
    if (Math.abs(delta) < threshold && (best.guide === null || Math.abs(delta) < Math.abs(best.delta))) {
      best = { delta, guide: target };
    }
  }
  return best;
}

/** Pull a moving layer's rect onto the composition's edges and midlines.
 *
 *  Purely a translation — width and height never change, so a snapped layer
 *  keeps its aspect. Each axis snaps independently, so a layer can be flush
 *  left and dead-centre vertically at the same time.
 *
 *  Thresholds are per-axis fractions of the composition, because the same
 *  on-screen pixel distance is a different fraction of a portrait composition's
 *  width than of its height. A zero threshold disables the axis. */
export function computeSnap(
  { rect, thresholdX, thresholdY }:
  { rect: LayerRect; thresholdX: number; thresholdY: number },
): { rect: LayerRect; guides: SnapGuides } {
  const x = snapAxis(rect.x, rect.w, thresholdX);
  const y = snapAxis(rect.y, rect.h, thresholdY);
  return {
    rect: { x: rect.x + x.delta, y: rect.y + y.delta, w: rect.w, h: rect.h },
    guides: { x: x.guide, y: y.guide },
  };
}
